<template>
  <v-container fluid class="pa-0 bg-light-soft min-h-screen">
    <!-- Header -->
    <v-row align="center" justify="center">
      <v-col cols="12" md="auto" class="my-0 py-0">
        <div class="d-flex align-center justify-center">
          <v-avatar color="primary" size="44" class="mr-3 elevation-2">
            <v-icon color="white">mdi-swap-horizontal</v-icon>
          </v-avatar>
          <div>
            <div class="text-h5 font-weight-bold grey--text text--darken-2">Transacciones</div>
            <div class="text-caption grey--text">Monitoreo y gestión de movimientos bancarios y pagos a suplidores</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Filtros: unidad de negocio y rango de tiempo -->
    <v-row justify="center" align="center">
      <v-col cols="12" md="auto" class="flex-grow-1">
        <search-header-po @dataFilters="handleFilters($event)" :show_location="false" />
      </v-col>
    </v-row>

    <!-- Acceso al trabajo pendiente (se abre en diálogo) -->
    <div v-if="can.create || can.approve" class="pending-banner mb-4" :class="{ 'pending-banner--clear': !pendingLoading && pendingSummary.total === 0 }"
      @click="openPendingDialog()">
      <v-avatar size="40" :color="pendingSummary.total ? 'orange lighten-5' : 'success lighten-5'" class="mr-3">
        <v-icon :color="pendingSummary.total ? 'orange darken-2' : 'success'">
          {{ pendingSummary.total ? "mdi-clipboard-clock-outline" : "mdi-clipboard-check-outline" }}
        </v-icon>
      </v-avatar>
      <div class="flex-grow-1 min-w-0">
        <div class="text-subtitle-2 font-weight-bold grey--text text--darken-3">
          <template v-if="pendingLoading">Buscando trabajo pendiente...</template>
          <template v-else-if="pendingSummary.total">Tienes trabajo pendiente en {{ pendingSummary.accounts }} cuenta{{ pendingSummary.accounts === 1 ? "" : "s" }}</template>
          <template v-else>Sin trabajo pendiente</template>
        </div>
        <div class="d-flex flex-wrap pending-banner__chips mt-1">
          <v-chip v-if="pendingSummary.cheques" x-small label color="blue lighten-5" text-color="blue darken-3">
            <v-icon x-small left>mdi-bank-check</v-icon>{{ pendingSummary.cheques }} cheques
          </v-chip>
          <v-chip v-if="pendingSummary.transfers" x-small label color="teal lighten-5" text-color="teal darken-3">
            <v-icon x-small left>mdi-bank-transfer</v-icon>{{ pendingSummary.transfers }} transferencias
          </v-chip>
          <v-chip v-if="pendingSummary.toApprove" x-small label color="orange lighten-5" text-color="orange darken-3">
            <v-icon x-small left>mdi-account-clock-outline</v-icon>{{ pendingSummary.toApprove }} por aprobar
          </v-chip>
          <v-chip v-if="pendingSummary.late" x-small label color="red lighten-5" text-color="red darken-3">
            <v-icon x-small left>mdi-alert-outline</v-icon>{{ pendingSummary.late }} con más de 30 días
          </v-chip>
          <span v-if="!pendingLoading && !pendingSummary.total" class="text-caption grey--text">
            No hay pagos por transaccionar ni transacciones por aprobar
          </span>
        </div>
      </div>
      <v-btn :color="pendingSummary.total ? 'orange darken-2' : 'success'" depressed dark class="rounded-lg text-none ml-3"
        :loading="pendingLoading">
        <v-icon left>mdi-format-list-checks</v-icon>
        Ver trabajo pendiente
        <v-chip v-if="pendingSummary.total" x-small color="white" class="ml-2 orange--text text--darken-3 font-weight-bold">
          {{ pendingSummary.total }}
        </v-chip>
      </v-btn>
    </div>

    <!-- Resumen del rango -->
    <v-row class="mb-4" dense>
      <v-col cols="6" md="3">
        <div class="kpi-tile" :class="{ 'kpi-tile--active': statusFilter === null }" @click="statusFilter = null">
          <div class="kpi-tile__label"><v-icon x-small color="blue" class="mr-1">mdi-format-list-numbered</v-icon>Registros</div>
          <div class="kpi-tile__value">{{ typedTransactions.length }}</div>
          <div class="kpi-tile__hint">en el rango seleccionado</div>
        </div>
      </v-col>
      <v-col v-for="kpi in statusKpis" :key="kpi.status" cols="6" md="3">
        <div class="kpi-tile" :class="{ 'kpi-tile--active': statusFilter === kpi.status }" @click="toggleStatus(kpi.status)">
          <div class="kpi-tile__label">
            <v-icon x-small :color="kpi.color" class="mr-1">{{ kpi.icon }}</v-icon>{{ kpi.label }}
          </div>
          <div class="kpi-tile__value" :class="`${kpi.color}--text`">{{ kpi.count }}</div>
          <div class="kpi-tile__hint text-truncate">
            <span v-for="(total, i) in kpi.totals" :key="total.code">
              <span v-if="i > 0"> · </span>{{ currencyFormat(total.amount) }} {{ total.code }}
            </span>
            <span v-if="!kpi.totals.length">—</span>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Tabla principal -->
    <v-card class="rounded-xl border-soft shadow-sm bg-white overflow-hidden" elevation="0">
      <div class="d-flex flex-wrap align-center px-4 py-3 toolbar-gap">
        <v-autocomplete
          v-model="accountBank"
          :items="bussinessAccount"
          item-text="text"
          item-value="account_id"
          label="Cuenta de banco"
          placeholder="Todas las cuentas"
          persistent-placeholder
          outlined dense hide-details clearable
          :disabled="bussinessAccount.length === 0"
          @change="get_transactions(lastData)"
          class="rounded-lg bg-light-soft toolbar-field"
        >
          <template v-slot:prepend-inner>
            <v-icon color="primary" size="20">mdi-bank-outline</v-icon>
          </template>
        </v-autocomplete>
        <v-select
          v-model="paymentTypeFilter"
          :items="paymentTypeOptions"
          item-text="name"
          item-value="id"
          label="Tipo de pago"
          placeholder="Todos los tipos"
          persistent-placeholder
          outlined dense hide-details clearable
          class="rounded-lg bg-light-soft toolbar-field toolbar-field--narrow"
        >
          <template v-slot:prepend-inner>
            <v-icon color="secondary" size="20">{{ paymentTypeFilter ? getIconPayments(paymentTypeFilter) : "mdi-cash-multiple" }}</v-icon>
          </template>
          <template v-slot:item="{ item }">
            <v-icon small left>{{ getIconPayments(item.id) }}</v-icon>
            {{ item.name }}
            <v-spacer></v-spacer>
            <span class="text-caption grey--text ml-3">{{ item.count }}</span>
          </template>
        </v-select>
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Buscar por ID, suplidor, banco, usuario..."
          hide-details outlined dense clearable class="rounded-lg bg-light-soft toolbar-field toolbar-field--wide"></v-text-field>
        <v-chip v-if="statusFilter !== null" close color="primary" outlined @click:close="statusFilter = null">
          Estado: {{ statusLabel(statusFilter) }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn color="success" outlined depressed class="rounded-lg text-capitalize px-4"
          :disabled="filteredTransactions.length === 0" @click="exportExcel()">
          <v-icon left size="18">mdi-microsoft-excel</v-icon>
          Excel
        </v-btn>
        <v-btn v-if="can.create" color="primary" depressed class="rounded-lg text-capitalize px-6" @click="openNewTransaction()">
          <v-icon left>mdi-plus</v-icon>
          Nueva Transacción
        </v-btn>
      </div>

      <v-divider></v-divider>

      <v-data-table :search="search" :headers="headersTransactions" :items="filteredTransactions"
        :loading="loading" loading-text="Cargando transacciones..." item-key="payment_transaction_id"
        class="custom-table" locale="es-do" :items-per-page="15"
        :footer-props="{ itemsPerPageOptions: [15, 30, 50, 100, -1], itemsPerPageText: 'Filas por página', itemsPerPageAllText: 'Todas' }"
        no-data-text="No hay transacciones en el rango seleccionado"
        no-results-text="Ninguna transacción coincide con la búsqueda">
        <template v-slot:[`item.payment_transaction_id`]="{ item }">
          <div @click="showTransactions(item.payment_transaction_id, 2)" class="d-flex align-center cursor-pointer py-2">
            <v-avatar size="24" color="primary lighten-5" class="mr-2">
              <span class="text-tiny font-weight-black primary--text">TX</span>
            </v-avatar>
            <span class="font-weight-black primary--text text-decoration-underline">
              #{{ filLeft(item.payment_transaction_id.toString(), 8, "0") }}
            </span>
          </div>
        </template>

        <template v-slot:[`item._suppliers_text`]="{ item }">
          <div class="d-flex flex-column py-2 supplier-cell">
            <span class="text-body-2 font-weight-medium grey--text text--darken-3 text-truncate">
              {{ item._suppliers[0] || "—" }}
            </span>
            <span class="text-tiny grey--text">
              <span v-if="item._suppliers.length > 1">+{{ item._suppliers.length - 1 }} suplidor{{ item._suppliers.length > 2 ? "es" : "" }} · </span>
              {{ item._payments_count }} pago{{ item._payments_count === 1 ? "" : "s" }}
            </span>
          </div>
        </template>

        <template v-slot:[`item.business_unit_description`]="{ item }">
          <span class="text-body-2 grey--text text--darken-3">{{ item.business_unit_description }}</span>
        </template>

        <template v-slot:[`item.payment_type_name`]="{ item }">
          <v-chip outlined small class="font-weight-bold" color="secondary">
            <v-icon x-small left>{{ getIconPayments(item.invoice_payment_type_id) }}</v-icon>
            {{ item.payment_type_name }}
          </v-chip>
        </template>

        <template v-slot:[`item.bank_account_description_long`]="{ item }">
          <div class="d-flex align-center py-2">
            <v-avatar size="28" color="white" class="border shadow-xs mr-3 overflow-hidden">
              <img v-if="bankLogo(item.bank_id)" :src="bankLogo(item.bank_id)" class="bank-logo" alt="" />
              <v-icon v-else small color="primary">mdi-bank-outline</v-icon>
            </v-avatar>
            <div class="d-flex flex-column min-w-0">
              <span class="text-body-2 font-weight-bold grey--text text--darken-4 text-truncate" style="max-width: 170px">
                {{ item.bank_account_description_long }}
              </span>
              <span class="text-tiny grey--text text-truncate" style="max-width: 170px">{{ item.account_name }}</span>
            </div>
          </div>
        </template>

        <template v-slot:[`item.payment_by_name`]="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="24" color="grey lighten-4" class="mr-2">
              <span class="text-tiny font-weight-black grey--text text--darken-2">{{ getInitials(item.payment_by_name) }}</span>
            </v-avatar>
            <span class="text-body-2 grey--text text--darken-3">{{ item.payment_by_name }}</span>
          </div>
        </template>

        <template v-slot:[`item.payment_date`]="{ item }">
          <div class="d-flex flex-column text-no-wrap">
            <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{ getDate(item.payment_date) }}</span>
            <span class="text-tiny grey--text">Registro: {{ getDate(item.created_date) }}</span>
          </div>
        </template>

        <template v-slot:[`item.payment_transaction_amount`]="{ item }">
          <div class="text-right text-no-wrap">
            <span class="text-body-2 font-weight-black grey--text text--darken-4">
              {{ currencyFormat(item.payment_transaction_amount) }}
            </span>
            <span class="text-tiny grey--text font-weight-bold ml-1">{{ item._currency }}</span>
          </div>
        </template>

        <template v-slot:[`item.status_id`]="{ item }">
          <v-chip outlined small class="font-weight-bold" :color="getColor(item.status_id)">
            <v-icon x-small left>mdi-circle</v-icon>
            {{ item.status_name }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-end">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small color="primary" v-bind="attrs" v-on="on"
                  @click="showTransactions(item.payment_transaction_id, 2)">
                  <v-icon small>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
              <span>Ver detalle</span>
            </v-tooltip>
            <v-tooltip v-if="item.status_id == 1 && can.cancel" top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small color="error lighten-1" v-bind="attrs" v-on="on" @click="askCancel(item)">
                  <v-icon small>mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
              <span>Anular</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Trabajo pendiente por cuenta (pantalla completa) -->
    <v-dialog v-model="pendingDialog" fullscreen transition="dialog-bottom-transition">
      <v-card class="bg-light-soft dialog-full-height">
        <v-toolbar color="white" flat class="border-bottom sticky-header px-2 px-md-4">
          <v-avatar color="orange lighten-5" size="40" class="mr-3 rounded-lg">
            <v-icon color="orange darken-2">mdi-clipboard-clock-outline</v-icon>
          </v-avatar>
          <div class="min-w-0">
            <div class="text-h6 font-weight-black grey--text text--darken-4 text-truncate">Trabajo pendiente</div>
            <div class="text-caption grey--text text-truncate">
              {{ pendingScopeLabel }} · sin importar la fecha
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon :loading="pendingLoading" @click="loadPending()">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon @click="pendingDialog = false" class="bg-light-soft ml-1">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-progress-linear v-if="pendingLoading" indeterminate color="orange" height="2"></v-progress-linear>

        <v-container fluid class="pa-3 pa-md-6">
          <!-- Resumen -->
          <v-row dense class="mb-2">
            <v-col v-for="tile in pendingTiles" :key="tile.key" cols="6" md="3">
              <div class="kpi-tile" :class="{ 'kpi-tile--active': pendingType === tile.key }" @click="pendingType = pendingType === tile.key ? 'all' : tile.key">
                <div class="kpi-tile__label">
                  <v-icon x-small :color="tile.color" class="mr-1">{{ tile.icon }}</v-icon>{{ tile.label }}
                </div>
                <div class="kpi-tile__value" :class="`${tile.color}--text`">{{ tile.count }}</div>
                <div class="kpi-tile__hint text-truncate">
                  <span v-for="(total, i) in tile.totals" :key="total.code">
                    <span v-if="i > 0"> · </span>{{ currencyFormat(total.amount) }} {{ total.code }}
                  </span>
                  <span v-if="!tile.totals.length">{{ tile.hint }}</span>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Filtros del diálogo -->
          <div class="d-flex flex-wrap align-center toolbar-gap mb-4">
            <v-btn-toggle v-model="pendingType" mandatory dense color="primary" class="rounded-lg flex-wrap">
              <v-btn value="all" small class="text-none">Todo</v-btn>
              <v-btn value="3" small class="text-none"><v-icon small left>mdi-bank-check</v-icon>Cheques</v-btn>
              <v-btn value="4" small class="text-none"><v-icon small left>mdi-bank-transfer</v-icon>Transferencias</v-btn>
              <v-btn value="approve" small class="text-none"><v-icon small left>mdi-account-clock-outline</v-icon>Por aprobar</v-btn>
            </v-btn-toggle>
            <v-text-field v-model="pendingSearch" prepend-inner-icon="mdi-magnify" placeholder="Buscar cuenta o banco"
              outlined dense hide-details clearable class="rounded-lg bg-white toolbar-field"></v-text-field>
            <v-spacer></v-spacer>
            <span class="text-caption grey--text">Ordenado por lo más atrasado primero</span>
          </div>

          <div v-if="!pendingLoading && visiblePendingAccounts.length === 0" class="text-center py-16">
            <v-icon size="72" color="success lighten-1">mdi-clipboard-check-outline</v-icon>
            <div class="text-h6 grey--text text--darken-1 mt-3">Todo al día</div>
            <div class="text-body-2 grey--text">No hay trabajo pendiente con los filtros seleccionados.</div>
          </div>

          <!-- Una sección por unidad de negocio (solo si hay varias) -->
          <div v-for="section in pendingSections" :key="section.business_unit_id" class="mb-6">
            <div v-if="pendingSections.length > 1" class="d-flex align-center mb-2">
              <v-icon small color="grey darken-1" class="mr-2">mdi-office-building-outline</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2">{{ businessName(section.business_unit_id) }}</span>
              <v-chip x-small outlined class="ml-2">{{ section.accounts.length }} cuenta{{ section.accounts.length === 1 ? "" : "s" }}</v-chip>
            </div>
            <v-row dense>
              <v-col v-for="account in section.accounts" :key="account.key" cols="12" sm="6" lg="4" xl="3">
                <div class="pending-card">
                  <div class="d-flex align-center mb-2">
                    <v-avatar size="34" color="white" class="border shadow-xs mr-2 overflow-hidden">
                      <img v-if="bankLogo(account.bank_id)" :src="bankLogo(account.bank_id)" class="bank-logo" alt="" />
                      <v-icon v-else small color="primary">mdi-bank-outline</v-icon>
                    </v-avatar>
                    <div class="flex-grow-1 min-w-0">
                      <div class="text-body-2 font-weight-bold grey--text text--darken-4 text-truncate">{{ account.account_name }}</div>
                      <div class="text-tiny grey--text text-truncate">{{ account.bank_name }}</div>
                    </div>
                    <v-chip x-small :color="account.late ? 'red lighten-5' : 'grey lighten-4'"
                      :text-color="account.late ? 'red darken-3' : 'grey darken-2'" class="font-weight-bold">
                      {{ account.pendingCount }}
                    </v-chip>
                  </div>

                  <div v-for="group in account.visibleGroups" :key="group.type" class="pending-line">
                    <v-icon small color="secondary" class="mr-2">{{ getIconPayments(group.type) }}</v-icon>
                    <div class="flex-grow-1 min-w-0">
                      <div class="text-caption font-weight-bold grey--text text--darken-3">
                        {{ group.count }} {{ paymentTypeLabel(group.type, group.count) }} por transaccionar
                      </div>
                      <div class="text-caption grey--text text--darken-1">
                        <span v-for="(total, i) in group.totals" :key="total.code">
                          <span v-if="i > 0"> · </span>{{ currencyFormat(total.amount) }} {{ total.code }}
                        </span>
                      </div>
                      <div class="text-tiny" :class="ageClass(group.oldest_date)">
                        Más antiguo: {{ getDate(group.oldest_date) }} ({{ ageText(group.oldest_date) }})
                      </div>
                    </div>
                    <v-btn v-if="can.create" small depressed color="primary" class="text-none ml-2"
                      @click="startFromPending(account, group.type)">
                      Transaccionar
                    </v-btn>
                  </div>

                  <div v-if="can.approve && account.toApprove > 0 && pendingType !== '3' && pendingType !== '4'"
                    class="pending-line pending-line--approve" @click="approveFromPending(account)">
                    <v-icon small color="orange darken-2" class="mr-2">mdi-account-clock-outline</v-icon>
                    <div class="flex-grow-1 text-caption font-weight-bold orange--text text--darken-3">
                      {{ account.toApprove }} {{ account.toApprove === 1 ? "transacción" : "transacciones" }} por aprobar
                      <div class="text-tiny grey--text font-weight-regular">
                        Desde {{ getDate(account.toApproveOldest) }} ({{ ageText(account.toApproveOldest) }}) · clic para verlas
                      </div>
                    </div>
                    <v-icon small color="orange darken-2">mdi-chevron-right</v-icon>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Nueva transacción -->
    <v-dialog transition="dialog-bottom-transition" v-model="dialog" fullscreen overlay-opacity="0.9">
      <v-card class="bg-light-soft dialog-full-height">
        <v-toolbar color="white" flat class="border-bottom sticky-header px-4">
          <v-avatar color="primary lighten-5" size="40" class="mr-3 rounded-lg">
            <v-icon color="primary">mdi-cash-plus</v-icon>
          </v-avatar>
          <div>
            <span class="text-h6 font-weight-black grey--text text--darken-4">Nueva Transacción</span>
            <p class="text-caption grey--text font-weight-bold mb-0 mt-n1 uppercase letter-spacing-1">CxP - Cuentas por Pagar</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false" class="bg-light-soft" :disabled="saving">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-container fluid class="pa-6">
          <v-row>
            <v-col cols="12" md="8">
              <v-card class="rounded-xl border-soft shadow-sm mb-6 bg-white overflow-hidden" elevation="0">
                <v-toolbar flat color="white" dense class="px-2">
                  <v-icon small color="primary" class="mr-2">mdi-account-search-outline</v-icon>
                  <span class="text-subtitle-2 font-weight-black grey--text text--darken-3">Filtros de Búsqueda</span>
                </v-toolbar>
                <v-divider></v-divider>
                <v-card-text class="pa-6 bg-light-soft">
                  <search-header ref="dialogHeader" :key="dialogKey" :show_department="false" :show_location="false"
                    :show_accounts="true" :select_week="2" :show_payments="true" :show_weeks="false"
                    :showAdditionalFilter="true" :allowed_payment_types="[3, 4]" :default_payment_type="4"
                    :require_selection="true" @dataFilters="pendingTrasactions($event, 1)" />
                </v-card-text>
              </v-card>

              <v-card class="rounded-xl border-soft shadow-sm bg-white overflow-hidden" elevation="0">
                <v-toolbar flat color="white" class="px-4">
                  <v-icon small color="primary" class="mr-2">mdi-format-list-checks</v-icon>
                  <v-toolbar-title class="text-subtitle-1 font-weight-black grey--text text--darken-4">Pagos Pendientes</v-toolbar-title>
                  <v-chip x-small outlined color="primary" class="ml-2">{{ paymentsTotal }}</v-chip>
                  <v-spacer></v-spacer>
                  <v-text-field v-model="searchPayments" prepend-inner-icon="mdi-magnify" label="Suplidor, RNC o # de pago"
                    hide-details outlined dense class="rounded-lg bg-light-soft" style="max-width: 280px"
                    @input="onSearchPayments" clearable @click:clear="onSearchPayments"></v-text-field>
                </v-toolbar>

                <v-divider></v-divider>

                <v-data-table v-model="selected" item-key="invoice_payment_id" show-select
                  :headers="gruposTransactions" :items="transactions" :loading="paymentsLoading"
                  loading-text="Cargando pagos pendientes..." class="custom-table" locale="es-do"
                  hide-default-footer :items-per-page="paymentsLimit"
                  :no-data-text="missingTransactionFilters.length ? 'Selecciona la cuenta de banco y el método de pago' : 'No hay pagos pendientes para esta cuenta'">
                  <template v-slot:[`item.invoice_payment_id`]="{ item }">
                    <div class="d-flex align-center py-2" @click="showPayments(item.invoice_payment_id)">
                      <v-avatar size="24" color="primary lighten-5" class="mr-2">
                        <span class="text-tiny font-weight-black primary--text">PY</span>
                      </v-avatar>
                      <span class="primary--text font-weight-black text-decoration-underline cursor-pointer">
                        #PAY-{{ filLeft(item.invoice_payment_id.toString(), 8, "0") }}
                      </span>
                    </div>
                  </template>

                  <template v-slot:[`item.entity`]="{ item }">
                    <div class="d-flex flex-column py-2">
                      <div v-for="(entityGroup, entityId) in groupByEntityId(item.payment_details)" :key="entityId"
                        class="d-flex align-center mb-1">
                        <v-avatar size="20" :color="entityGroup[0].supplier_account ? 'primary lighten-5' : 'error lighten-5'" class="mr-2">
                          <span class="text-tiny font-weight-black" :class="entityGroup[0].supplier_account ? 'primary--text' : 'error--text'">{{
                            getInitials(entityGroup[0].entity_name) }}</span>
                        </v-avatar>
                        <span class="text-caption font-weight-bold text-truncate"
                          :class="entityGroup[0].supplier_account ? 'grey--text text--darken-3' : 'error--text'" style="max-width: 280px">
                          {{ entityGroup[0].entity_name }}
                        </span>
                        <v-tooltip v-if="!entityGroup[0].supplier_account" top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon x-small color="error" class="ml-1" v-bind="attrs" v-on="on">mdi-bank-off-outline</v-icon>
                          </template>
                          <span>Sin cuenta bancaria registrada</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </template>

                  <template v-slot:[`item.payment_by_name`]="{ item }">
                    <div class="d-flex align-center">
                      <v-avatar size="20" color="grey lighten-4" class="mr-2">
                        <span class="text-tiny font-weight-black grey--text text--darken-2">{{ getInitials(item.payment_by_name) }}</span>
                      </v-avatar>
                      <div class="d-flex flex-column">
                        <span class="text-caption grey--text text--darken-4 font-weight-bold">{{ item.payment_by_name }}</span>
                        <span class="text-tiny grey--text">{{ getDate(item.payment_date || item.created_date) }}</span>
                      </div>
                    </div>
                  </template>

                  <template v-slot:[`item.total_amount`]="{ item }">
                    <div class="text-right text-no-wrap">
                      <span class="text-body-2 font-weight-black grey--text text--darken-4">{{ currencyFormat(item.total_amount) }}</span>
                      <span class="text-tiny grey--text font-weight-bold ml-1">{{ item.currency_code }}</span>
                    </div>
                  </template>

                  <template v-slot:[`item.payment_type_name`]="{ item }">
                    <v-chip outlined x-small class="font-weight-bold" color="secondary">
                      <v-icon x-small left>{{ getIconPayments(item.payment_type_id || item.invoice_payment_type_id) }}</v-icon>
                      {{ item.payment_type_name }}
                    </v-chip>
                  </template>

                  <template v-slot:footer>
                    <v-divider></v-divider>
                    <div class="pa-3">
                      <v-row justify="center" align="center" no-gutters>
                        <v-col cols="auto" class="text-caption grey--text mr-4">
                          <span v-if="paymentsTotal">
                            {{ (paymentsPage - 1) * paymentsLimit + 1 }}–{{ Math.min(paymentsPage * paymentsLimit, paymentsTotal) }} de {{ paymentsTotal }} pagos
                          </span>
                        </v-col>
                        <v-col cols="auto">
                          <v-pagination v-model="paymentsPage" :length="paymentsTotalPages" :total-visible="6"
                            color="primary" circle :disabled="paymentsLoading" @input="onPaymentsPageChange">
                          </v-pagination>
                        </v-col>
                      </v-row>
                    </div>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>

            <v-col cols="12" md="4">
              <v-card class="rounded-xl border-soft shadow-sm bg-white overflow-hidden sticky-config" elevation="0">
                <v-toolbar flat color="primary" dark dense>
                  <v-icon small class="mr-2">mdi-cog-outline</v-icon>
                  <span class="text-subtitle-2 font-weight-black">Configuración de Pago</span>
                </v-toolbar>

                <v-card-text class="pa-6">
                  <div class="mb-6">
                    <label class="text-caption font-weight-black grey--text text-uppercase letter-spacing-1 d-block mb-2 text-center">Fechas de Transacción</label>

                    <v-menu v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="date" label="Fecha de Aplicación" prepend-inner-icon="mdi-calendar-export"
                          readonly v-bind="attrs" v-on="on" outlined dense class="rounded-lg mb-4" hide-details></v-text-field>
                      </template>
                      <v-date-picker v-model="date" @input="menu = false" color="primary" locale="es"></v-date-picker>
                    </v-menu>

                    <v-menu v-model="menu2" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="date2" label="Fecha Contable" prepend-inner-icon="mdi-calendar-check"
                          readonly v-bind="attrs" v-on="on" outlined dense class="rounded-lg" hide-details></v-text-field>
                      </template>
                      <v-date-picker v-model="date2" @input="menu2 = false" color="primary" locale="es"></v-date-picker>
                    </v-menu>
                  </div>

                  <v-divider class="mb-4"></v-divider>

                  <!-- Resumen de lo seleccionado -->
                  <div class="mb-4">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <span class="text-caption font-weight-bold grey--text">Pagos seleccionados</span>
                      <span class="text-h6 font-weight-black primary--text">{{ selected.length }}</span>
                    </div>
                    <div v-for="total in selectedTotals" :key="total.code" class="d-flex justify-space-between align-center">
                      <span class="text-caption font-weight-bold grey--text">Total {{ total.code }}</span>
                      <span class="text-h6 font-weight-black success--text">{{ currencyFormat(total.amount) }}</span>
                    </div>
                  </div>

                  <div v-if="selectedBySupplier.length" class="selected-list mb-4">
                    <div v-for="supplier in selectedBySupplier" :key="supplier.name" class="selected-list__row">
                      <div class="min-w-0">
                        <div class="text-caption font-weight-bold grey--text text--darken-3 text-truncate"
                          :class="{ 'error--text': !supplier.hasAccount }">
                          <v-icon v-if="!supplier.hasAccount" x-small color="error">mdi-bank-off-outline</v-icon>
                          {{ supplier.name }}
                        </div>
                        <div class="text-tiny grey--text">{{ supplier.payments }} pago{{ supplier.payments === 1 ? "" : "s" }}</div>
                      </div>
                      <div class="text-caption font-weight-bold text-no-wrap">
                        <div v-for="total in supplier.totals" :key="total.code">{{ currencyFormat(total.amount) }} {{ total.code }}</div>
                      </div>
                    </div>
                  </div>

                  <v-btn block x-large color="primary" depressed class="rounded-xl font-weight-black text-capitalize"
                    @click="goTransation()" :disabled="!canCreateTransaction" :loading="saving">
                    Realizar Transacción
                    <v-icon right>mdi-arrow-right-circle</v-icon>
                  </v-btn>
                </v-card-text>

                <v-alert v-if="missingTransactionFilters.length > 0" type="error" text tile
                  class="mb-0 text-caption font-weight-medium" icon="mdi-alert-circle-outline">
                  <strong>Requerido para registrar la transacción:</strong>
                  <div v-for="label in missingTransactionFilters" :key="label" class="mt-1">• {{ label }}</div>
                </v-alert>

                <v-alert v-else-if="selectedTotals.length > 1" type="error" text tile
                  class="mb-0 text-caption font-weight-medium" icon="mdi-cash-multiple">
                  <strong>Monedas mezcladas:</strong> selecciona pagos de una sola moneda para registrar la transacción.
                </v-alert>

                <v-alert v-else-if="selectedMissingAccounts.length > 0" type="warning" text tile
                  class="mb-0 text-caption font-weight-medium" icon="mdi-bank-off-outline">
                  <strong>{{ selectedMissingAccounts.length }} suplidor(es) sin cuenta bancaria:</strong>
                  <div v-for="name in selectedMissingAccounts" :key="name" class="mt-1">• {{ name }}</div>
                </v-alert>

                <v-alert v-else-if="selected.length === 0" type="info" text tile class="mb-0 text-caption font-weight-medium"
                  icon="mdi-information-outline">
                  Seleccione al menos un pago pendiente para habilitar el proceso.
                </v-alert>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Confirmar anulación -->
    <v-dialog v-model="dialogCancelPayment" max-width="450" overlay-opacity="0.9" :persistent="cancelling">
      <v-card class="rounded-xl overflow-hidden shadow-xl" elevation="0">
        <div class="pa-8 text-center bg-light-soft">
          <v-avatar color="error lighten-5" size="80" class="mb-6">
            <v-icon color="error" size="40">mdi-alert-circle-outline</v-icon>
          </v-avatar>
          <h2 class="text-h5 font-weight-black grey--text text--darken-4 mb-2">¿Anular transacción?</h2>
          <p class="text-body-2 grey--text font-weight-medium mb-4 px-4">
            Esta acción revertirá los pagos asociados y no podrá deshacerse fácilmente.
          </p>
          <div v-if="selectPayments" class="cancel-summary text-left">
            <div class="d-flex justify-space-between"><span class="grey--text">Transacción</span>
              <strong>#{{ filLeft(selectPayments.payment_transaction_id.toString(), 8, "0") }}</strong></div>
            <div class="d-flex justify-space-between"><span class="grey--text">Banco</span>
              <strong class="text-right ml-4">{{ selectPayments.bank_account_description_long }}</strong></div>
            <div class="d-flex justify-space-between"><span class="grey--text">Suplidores</span>
              <strong class="text-right ml-4">{{ selectPayments._suppliers.length }}</strong></div>
            <div class="d-flex justify-space-between"><span class="grey--text">Monto</span>
              <strong>{{ currencyFormat(selectPayments.payment_transaction_amount) }} {{ selectPayments._currency }}</strong></div>
          </div>
        </div>

        <v-card-actions class="pa-6 border-top bg-white d-flex flex-column">
          <v-btn block depressed x-large class="rounded-xl text-capitalize font-weight-bold mb-3 grey--text text--darken-2"
            color="grey lighten-4" :disabled="cancelling" @click="dialogCancelPayment = false">
            Mantener Transacción
          </v-btn>
          <v-btn block depressed x-large class="rounded-xl text-capitalize font-weight-bold error--text ml-0"
            color="error lighten-5" :loading="cancelling" @click="confirmCancel()">
            Confirmar Anulación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Aviso de transacción creada -->
    <v-snackbar v-model="createdSnackbar" color="success" timeout="10000" right bottom>
      Transacción #{{ createdTransactionId ? filLeft(String(createdTransactionId), 8, "0") : "" }} registrada
      <template v-slot:action="{ attrs }">
        <v-btn v-if="createdTransactionId" text v-bind="attrs" @click="showTransactions(createdTransactionId, 2)">Ver</v-btn>
        <v-btn icon v-bind="attrs" @click="createdSnackbar = false"><v-icon small>mdi-close</v-icon></v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import moment from "moment";
import global_reports from "@/services/global_reports.js";

const PAYMENT_TYPE_LABELS = { 3: ["cheque", "cheques"], 4: ["transferencia", "transferencias"] };
// Logos disponibles en /public; los demás bancos muestran un icono
const PUBLIC_BANK_LOGOS = ["popular.jpg", "banreservas.png"];

// Suma montos agrupando por moneda: nunca se mezclan monedas en un total
const totalsByCurrency = (rows, codeOf, amountOf) => {
  const map = {};
  for (const row of rows) {
    const code = codeOf(row) || "—";
    map[code] = (map[code] || 0) + (parseFloat(amountOf(row)) || 0);
  }
  return Object.entries(map).map(([code, amount]) => ({ code, amount }));
};

export default {
  data: () => ({
    date: moment().format("YYYY-MM-DD"),
    date2: moment().format("YYYY-MM-DD"),
    menu: false,
    menu2: false,
    dialog: false,
    dialogKey: 0,
    loading: false,
    paymentsLoading: false,
    saving: false,
    cancelling: false,
    pendingLoading: false,
    pendingDialog: false,
    pendingType: "all",
    pendingSearch: "",
    pending: { payments: [], transactions: [], accounts: [] },
    statusFilter: null,
    paymentTypeFilter: null,
    createdSnackbar: false,
    createdTransactionId: null,
    transactions: [],
    paymentsPage: 1,
    paymentsLimit: 15,
    paymentsTotal: 0,
    paymentsTotalPages: 0,
    searchPayments: "",
    searchPaymentsTimeout: null,
    dialogCancelPayment: false,
    search: "",
    selected: [],
    all_transactions: [],
    headersTransactions: [
      { text: "Id", align: "start", value: "payment_transaction_id" },
      { text: "Suplidores", value: "_suppliers_text", sortable: false },
      { text: "Unidad negocio", value: "business_unit_description" },
      { text: "Tipo pago", value: "payment_type_name" },
      { text: "Banco", value: "bank_account_description_long" },
      { text: "Usuario", value: "payment_by_name" },
      { text: "Fecha aplicación", value: "payment_date" },
      { text: "Monto total", align: "end", value: "payment_transaction_amount" },
      { text: "Status", value: "status_id" },
      { text: "", value: "actions", sortable: false, align: "end" },
    ],
    gruposTransactions: [
      { text: "Grupo", align: "start", sortable: false, value: "invoice_payment_id" },
      { text: "Suplidor", value: "entity", width: "30%", sortable: false },
      { text: "Tipo pago", value: "payment_type_name" },
      { text: "Usuario", value: "payment_by_name" },
      { text: "Monto total", align: "end", value: "total_amount" },
    ],
    accountBank: null,
    bussinessAccount: [],
    selectPayments: null,
    lastData: null,
    // Filtros del diálogo de nueva transacción (declarado para que sea reactivo)
    lasData: null,
  }),

  computed: {
    // Permisos por acción (CXP-012 crear, CXP-013 aprobar, CXP-015 anular)
    can() {
      return {
        create: this.hasFeature("CXP-012"),
        approve: this.hasFeature("CXP-013"),
        cancel: this.hasFeature("CXP-015"),
      };
    },
    // Transacciones con campos calculados para mostrar y buscar
    transactionRows() {
      return this.all_transactions.map((tx) => {
        const details = Array.isArray(tx.payment_transaction_details) ? tx.payment_transaction_details : [];
        const suppliers = [...new Set(details.map((d) => d.entity_name).filter(Boolean))];
        const payments = new Set();
        for (const detail of details) {
          for (const inv of detail.payment_transaction_invoice_details || []) {
            if (inv.invoice_payment_id) payments.add(inv.invoice_payment_id);
          }
        }
        return {
          ...tx,
          _suppliers: suppliers,
          _suppliers_text: suppliers.join(" "),
          _payments_count: payments.size || details.length,
          _currency: details.find((d) => d.currency_code)?.currency_code || "",
        };
      });
    },
    // Tipos de pago presentes en el rango, con cantidad
    paymentTypeOptions() {
      const map = new Map();
      for (const tx of this.transactionRows) {
        const id = tx.invoice_payment_type_id;
        if (id == null) continue;
        if (!map.has(id)) map.set(id, { id, name: tx.payment_type_name, count: 0 });
        map.get(id).count++;
      }
      return [...map.values()].sort((a, b) => b.count - a.count);
    },
    typedTransactions() {
      if (this.paymentTypeFilter == null) return this.transactionRows;
      return this.transactionRows.filter((tx) => tx.invoice_payment_type_id === this.paymentTypeFilter);
    },
    filteredTransactions() {
      if (this.statusFilter === null) return this.typedTransactions;
      return this.typedTransactions.filter((tx) => tx.status_id === this.statusFilter);
    },
    statusKpis() {
      const defs = [
        { status: 1, label: "Por aprobar", color: "orange", icon: "mdi-account-clock-outline" },
        { status: 2, label: "Aprobadas", color: "success", icon: "mdi-check-circle-outline" },
        { status: 4, label: "Anuladas", color: "error", icon: "mdi-cancel" },
      ];
      return defs.map((def) => {
        const rows = this.typedTransactions.filter((tx) => tx.status_id === def.status);
        return {
          ...def,
          count: rows.length,
          totals: totalsByCurrency(rows, (tx) => tx._currency, (tx) => tx.payment_transaction_amount),
        };
      });
    },

    // Tarjetas de trabajo pendiente, una por cuenta de banco
    pendingAccounts() {
      const accountInfo = new Map(this.pending.accounts.map((a) => [a.bank_account_id, a]));
      const map = new Map();
      const ensure = (row) => {
        const key = `${row.business_unit_id}-${row.bank_account_id}`;
        if (!map.has(key)) {
          const info = accountInfo.get(row.bank_account_id) || {};
          map.set(key, {
            key,
            business_unit_id: row.business_unit_id,
            bank_account_id: row.bank_account_id,
            account_name: info.account_name || (row.bank_account_id ? `Cuenta ${row.bank_account_id}` : "Sin cuenta"),
            bank_name: info.bank_name || "",
            bank_id: info.bank_id,
            groupsMap: {},
            toApprove: 0,
            toApproveOldest: null,
          });
        }
        return map.get(key);
      };
      for (const row of this.pending.payments) {
        const account = ensure(row);
        const type = row.invoice_payment_type_id;
        if (!account.groupsMap[type]) account.groupsMap[type] = { type, count: 0, rows: [], oldest_date: null };
        const group = account.groupsMap[type];
        group.count += row.count;
        group.rows.push(row);
        if (!group.oldest_date || moment(row.oldest_date).isBefore(group.oldest_date)) group.oldest_date = row.oldest_date;
      }
      for (const row of this.pending.transactions) {
        const account = ensure(row);
        account.toApprove += row.count;
        if (!account.toApproveOldest || moment(row.oldest_date).isBefore(account.toApproveOldest)) {
          account.toApproveOldest = row.oldest_date;
        }
      }
      return [...map.values()]
        .map((account) => {
          const groups = Object.values(account.groupsMap)
            .map((group) => ({
              ...group,
              totals: totalsByCurrency(group.rows, (r) => r.currency_code, (r) => r.amount),
            }))
            .sort((a, b) => a.type - b.type);
          const dates = [...groups.map((g) => g.oldest_date), account.toApproveOldest].filter(Boolean);
          const oldest = dates.length ? dates.reduce((a, b) => (moment(a).isBefore(b) ? a : b)) : null;
          return {
            ...account,
            groups,
            oldest,
            pendingCount: groups.reduce((sum, g) => sum + g.count, 0) + account.toApprove,
            late: groups.some((g) => this.ageDays(g.oldest_date) > 30),
          };
        })
        // Lo más atrasado primero
        .sort((a, b) => (a.oldest && b.oldest ? moment(a.oldest).diff(b.oldest) : b.pendingCount - a.pendingCount));
    },
    pendingSummary() {
      const summary = { total: 0, accounts: 0, cheques: 0, transfers: 0, toApprove: 0, late: 0 };
      // Cada usuario ve solo el trabajo que puede hacer
      for (const account of this.pendingAccounts) {
        const approve = this.can.approve ? account.toApprove : 0;
        const groups = this.can.create ? account.groups : [];
        if (!approve && !groups.length) continue;
        summary.accounts++;
        summary.toApprove += approve;
        for (const group of groups) {
          if (group.type === 3) summary.cheques += group.count;
          else if (group.type === 4) summary.transfers += group.count;
          if (this.ageDays(group.oldest_date) > 30) summary.late += group.count;
        }
      }
      summary.total = summary.cheques + summary.transfers + summary.toApprove;
      return summary;
    },
    pendingTiles() {
      const payments = this.pending.payments;
      const byType = (type) => payments.filter((p) => p.invoice_payment_type_id === type);
      const totals = (rows) => totalsByCurrency(rows, (r) => r.currency_code, (r) => r.amount);
      return [
        { key: "3", label: "Cheques", icon: "mdi-bank-check", color: "blue", count: this.pendingSummary.cheques, totals: totals(byType(3)), hint: "por transaccionar" },
        { key: "4", label: "Transferencias", icon: "mdi-bank-transfer", color: "teal", count: this.pendingSummary.transfers, totals: totals(byType(4)), hint: "por transaccionar" },
        { key: "approve", label: "Por aprobar", icon: "mdi-account-clock-outline", color: "orange", count: this.pendingSummary.toApprove, totals: [], hint: "transacciones registradas" },
        { key: "late", label: "Más de 30 días", icon: "mdi-alert-outline", color: "red", count: this.pendingSummary.late, totals: [], hint: "pagos atrasados" },
      ].filter((tile) => (tile.key === "approve" ? this.can.approve : this.can.create));
    },
    visiblePendingAccounts() {
      const term = (this.pendingSearch || "").trim().toUpperCase();
      return this.pendingAccounts
        .map((account) => {
          let visibleGroups = this.can.create ? account.groups : [];
          if (this.pendingType === "3" || this.pendingType === "4") {
            visibleGroups = account.groups.filter((g) => String(g.type) === this.pendingType);
          } else if (this.pendingType === "approve") {
            visibleGroups = [];
          } else if (this.pendingType === "late") {
            visibleGroups = account.groups.filter((g) => this.ageDays(g.oldest_date) > 30);
          }
          return { ...account, visibleGroups };
        })
        .filter((account) => {
          if (term && !`${account.account_name} ${account.bank_name}`.toUpperCase().includes(term)) return false;
          const canApprove = this.can.approve && account.toApprove > 0;
          if (this.pendingType === "approve") return canApprove;
          if (this.pendingType === "all") return canApprove || account.visibleGroups.length > 0;
          return account.visibleGroups.length > 0;
        });
    },
    pendingSections() {
      const map = new Map();
      for (const account of this.visiblePendingAccounts) {
        if (!map.has(account.business_unit_id)) map.set(account.business_unit_id, { business_unit_id: account.business_unit_id, accounts: [] });
        map.get(account.business_unit_id).accounts.push(account);
      }
      return [...map.values()];
    },
    pendingScopeLabel() {
      const ids = this.pendingBusinessIds();
      return ids.length === 1 ? this.businessName(ids[0]) : "Todas las unidades autorizadas";
    },

    // Filtros obligatorios para poder registrar una transacción.
    missingTransactionFilters() {
      const missing = [];
      const filters = this.lasData;
      if (!filters || !filters.business) missing.push("Unidad de negocio");
      if (!filters || !filters.accountBank) missing.push("Cuenta de banco");
      if (!filters || !filters.payments_id) missing.push("Método de pago");
      return missing;
    },
    selectedTotals() {
      return totalsByCurrency(this.selected, (p) => p.currency_code, (p) => p.total_amount);
    },
    selectedBySupplier() {
      const map = new Map();
      for (const payment of this.selected) {
        const names = [...new Set((payment.payment_details || []).map((d) => d.entity_name).filter(Boolean))];
        const name = names.length ? names.join(", ") : `Pago #${payment.invoice_payment_id}`;
        if (!map.has(name)) map.set(name, { name, payments: 0, rows: [], hasAccount: true });
        const entry = map.get(name);
        entry.payments++;
        entry.rows.push(payment);
        if ((payment.payment_details || []).some((d) => !d.supplier_account)) entry.hasAccount = false;
      }
      return [...map.values()].map((entry) => ({
        ...entry,
        totals: totalsByCurrency(entry.rows, (p) => p.currency_code, (p) => p.total_amount),
      }));
    },
    canCreateTransaction() {
      return (
        this.missingTransactionFilters.length === 0 &&
        this.selected.length > 0 &&
        this.selectedTotals.length <= 1 &&
        !this.saving
      );
    },
    selectedMissingAccounts() {
      const names = new Set();
      for (const item of this.selected) {
        if (!item.payment_details) continue;
        for (const detail of item.payment_details) {
          if (!detail.supplier_account) names.add(detail.entity_name);
        }
      }
      return [...names];
    },
  },

  methods: {
    getColor(status) {
      return { 1: "orange", 2: "green", 3: "deep-orange", 4: "error" }[status] || "grey";
    },
    statusLabel(status) {
      return { 1: "Por aprobar", 2: "Aprobadas", 4: "Anuladas" }[status] || status;
    },
    toggleStatus(status) {
      this.statusFilter = this.statusFilter === status ? null : status;
    },
    paymentTypeLabel(type, count) {
      const [one, many] = PAYMENT_TYPE_LABELS[type] || ["pago", "pagos"];
      return count === 1 ? one : many;
    },
    // Logo del banco solo si existe la imagen; si no, se muestra un icono
    bankLogo(bankId) {
      const img = this.getIconBank(bankId);
      return PUBLIC_BANK_LOGOS.includes(img) ? img : null;
    },
    businessName(id) {
      const unit = (this.$store.state.auth.bussines_units || []).find((u) => parseInt(u.value) === parseInt(id));
      return unit ? unit.text : `Unidad ${id}`;
    },
    ageDays(date) {
      return date ? Math.max(0, moment().startOf("day").diff(moment(date).startOf("day"), "days")) : 0;
    },
    ageText(date) {
      const days = this.ageDays(date);
      return days === 0 ? "hoy" : `hace ${days} día${days === 1 ? "" : "s"}`;
    },
    ageClass(date) {
      const days = this.ageDays(date);
      if (days > 30) return "error--text";
      if (days > 7) return "orange--text text--darken-2";
      return "grey--text";
    },
    groupByEntityId(array) {
      if (!array) return [];
      const grouped = {};
      array.forEach((item) => {
        if (!grouped[item.entity_id]) grouped[item.entity_id] = [];
        grouped[item.entity_id].push(item);
      });
      return grouped;
    },
    getInitials(name) {
      if (!name) return "??";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    },
    filLeft(str, len, char) {
      if (!str) return "0".repeat(len);
      return str.padStart(len, char);
    },
    getDate(item) {
      return item ? moment(item).format("DD/MM/YYYY") : "—";
    },

    onSearchPayments() {
      if (this.searchPaymentsTimeout) clearTimeout(this.searchPaymentsTimeout);
      this.searchPaymentsTimeout = setTimeout(() => {
        this.paymentsPage = 1;
        this.pendingTrasactions(this.lasData, 1, 1);
      }, 400);
    },

    // Abre el diálogo de nueva transacción. Con `preset` (desde las tarjetas de
    // pendientes) deja elegidas la unidad, la cuenta de banco y el método de pago.
    async openNewTransaction(preset = null) {
      const units = this.$store.state.auth.bussines_units || [];
      if (preset && preset.business_unit_id) {
        this.$store.commit("setBusiness", preset.business_unit_id);
      } else if (!this.$store.state.app.filters.business && units.length > 0) {
        this.$store.commit("setBusiness", units[0].value);
      }
      this.selected = [];
      this.searchPayments = "";
      this.lasData = null;
      this.transactions = [];
      this.dialogKey++;
      this.dialog = true;
      if (preset) await this.applyDialogPreset(preset);
    },
    async applyDialogPreset(preset) {
      const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      // El filtro del diálogo reinicia la cuenta mientras carga; se espera a que
      // termine y se reintenta hasta que la selección quede aplicada.
      for (let attempt = 0; attempt < 20; attempt++) {
        await wait(300);
        const header = this.$refs.dialogHeader;
        if (!header || !this.dialog) return;
        const hasAccount = (header.bussinessAccount || []).some((a) => a.account_id === preset.bank_account_id);
        if (!hasAccount || header.loading) continue;
        if (header.filters.accountBank !== preset.bank_account_id || header.filters.payments_id !== preset.payment_type) {
          header.filters.accountBank = preset.bank_account_id;
          header.filters.payments_id = preset.payment_type;
          header.updateData();
          continue;
        }
        if (this.lasData && this.lasData.accountBank === preset.bank_account_id && this.lasData.payments_id === preset.payment_type) {
          return;
        }
      }
    },

    async pendingTrasactions(data, type, page = 1) {
      // Guardar siempre los filtros para que las validaciones del diálogo reaccionen,
      // incluso cuando falta la unidad de negocio o la cuenta de banco.
      if (data) {
        const prev = this.lasData;
        this.lasData = data;
        if (!prev || data.accountBank !== prev.accountBank || data.business !== prev.business || data.payments_id !== prev.payments_id) {
          this.selected = [];
        }
      }

      if (!data || !data.business || !data.accountBank) {
        this.transactions = [];
        this.paymentsTotal = 0;
        this.paymentsTotalPages = 0;
        return;
      }

      let filter = "";
      if (type === 1) {
        filter = "where invoice_type_id IN (1,4) and status_id=1";
        filter += data.accountBank != null ? ` and bank_account_id=${parseInt(data.accountBank)}` : ``;
        filter += data.payments_id != null ? ` and invoice_payment_type_id=${parseInt(data.payments_id)}` : ``;
      }
      if (type === 2) {
        filter = ` where invoice_type_id IN (2,3) and status_id=3`;
      }

      this.paymentsLoading = true;
      try {
        const resultpayments = await this.api_Post("transactions/viewAllPayemnts_paginated", {
          filters: filter,
          page: page,
          limit: this.paymentsLimit,
          entity_search: (this.searchPayments || "").trim() || undefined,
        });
        if (resultpayments.status == 200) {
          const response = await resultpayments.json();
          this.transactions = response.data;
          this.paymentsTotal = response.total;
          this.paymentsTotalPages = response.totalPages;
          this.paymentsPage = response.page;
        }
      } finally {
        this.paymentsLoading = false;
      }
    },
    onPaymentsPageChange(page) {
      this.pendingTrasactions(this.lasData, 1, page);
    },

    async handleFilters(data) {
      const prevBusiness = this.lastData?.business;
      this.lastData = data;
      if (data.business !== prevBusiness) {
        this.accountBank = null;
        await this.loadBankAccounts(data.business);
        this.loadPending();
      }
      this.get_transactions(data);
    },

    // Unidades de negocio autorizadas al usuario (el store ya viene filtrado en el
    // login por acess_business_unit_finance + status activo).
    accessibleBusinessUnits() {
      const fromStore = (this.$store.state.auth.bussines_units || [])
        .map((unit) => parseInt(unit.value))
        .filter((id) => !isNaN(id));
      if (fromStore.length > 0) return fromStore;

      const fallback = this.$store.state.auth.user?.acess_business_unit_finance;
      const raw = Array.isArray(fallback) ? fallback : String(fallback || "").split(",");
      return raw.map((id) => parseInt(id)).filter((id) => !isNaN(id));
    },

    // Devuelve el fragmento SQL que acota la consulta por unidad de negocio.
    // Ojo: no existe la unidad 0; el 0 es solo el centinela de "Todas" del filtro
    // global, así que nunca debe llegar al SQL como id.
    buildBusinessScopeFilter(businessId) {
      const allowed = this.accessibleBusinessUnits();
      if (allowed.length === 0) return null;
      const selected = parseInt(businessId);
      if (!isNaN(selected) && selected !== 0) {
        if (!allowed.includes(selected)) return null;
        return ` and business_unit_id=${selected}`;
      }
      return ` and business_unit_id in (${allowed.join(",")})`;
    },

    // Unidades para el panel de pendientes: la seleccionada o todas las autorizadas
    pendingBusinessIds() {
      const allowed = this.accessibleBusinessUnits();
      const selected = parseInt(this.lastData?.business);
      if (!isNaN(selected) && selected !== 0) return allowed.includes(selected) ? [selected] : [];
      return allowed;
    },
    async loadPending() {
      const ids = this.pendingBusinessIds();
      if (ids.length === 0) {
        this.pending = { payments: [], transactions: [], accounts: [] };
        return;
      }
      this.pendingLoading = true;
      try {
        const result = await this.api_Post("transactions/cxp_pending_by_account", { business_ids: ids });
        if (result.status === 200) {
          const data = await result.json();
          this.pending = {
            payments: data.payments || [],
            transactions: data.transactions || [],
            accounts: data.accounts || [],
          };
        }
      } catch (error) {
        console.error("Error loading pending work:", error);
      } finally {
        this.pendingLoading = false;
      }
    },
    openPendingDialog() {
      this.pendingType = "all";
      this.pendingSearch = "";
      this.pendingDialog = true;
      this.loadPending();
    },
    startFromPending(account, type) {
      this.pendingDialog = false;
      this.openNewTransaction({
        business_unit_id: account.business_unit_id,
        bank_account_id: account.bank_account_id,
        payment_type: type,
      });
    },
    approveFromPending(account) {
      this.pendingDialog = false;
      this.focusPendingApproval(account);
    },
    focusPendingApproval(account) {
      this.statusFilter = 1;
      const isListed = this.bussinessAccount.some((a) => a.account_id === account.bank_account_id);
      if (isListed && this.accountBank !== account.bank_account_id) {
        this.accountBank = account.bank_account_id;
        this.get_transactions(this.lastData);
      }
      const visible = this.transactionRows.filter(
        (tx) => tx.status_id === 1 && (!isListed || tx.bank_account_id === account.bank_account_id)
      ).length;
      if (visible < account.toApprove) {
        this.$toast.info(
          `Hay ${account.toApprove} por aprobar desde ${this.getDate(account.toApproveOldest)}; amplía el rango de tiempo si no las ves todas.`
        );
      }
    },

    async loadBankAccounts(businessId) {
      const unitId = parseInt(businessId);
      // Solo se consultan cuentas de unidades autorizadas.
      if (!unitId || !this.accessibleBusinessUnits().includes(unitId)) {
        this.bussinessAccount = [];
        this.accountBank = null;
        return;
      }
      try {
        const result = await this.api_Post("humanResource/bussinessAccount", {
          filters: `where business_unit_id=${unitId}`,
        });
        if (result.status === 200) {
          const accounts = await result.json();
          this.bussinessAccount = accounts.map((account) => ({
            ...account,
            text: `${account.account_number} (${account.bank_name})`,
          }));
        }
      } catch (error) {
        console.error("Error loading bank accounts:", error);
      }
    },

    async get_transactions(data) {
      if (!data) return;

      // "Todas" no puede traducirse en ausencia de filtro: el backend devolvería
      // transacciones de TODAS las unidades de negocio, incluidas las que el
      // usuario no tiene autorizadas. Se sustituye por un IN con las accesibles.
      const businessScope = this.buildBusinessScopeFilter(data.business);
      if (businessScope === null) {
        this.all_transactions = [];
        this.loading = false;
        return;
      }

      this.loading = true;
      this.lastData = data;
      let filter = ` where payment_date between '${data.dateStart}' and '${data.dateEnd}' `;
      filter += businessScope;
      filter += this.accountBank != null ? ` and bank_account_id=${parseInt(this.accountBank)}` : ``;
      filter += data.payments_id != null ? ` and payment_type_id=${parseInt(data.payments_id)}` : ``;

      try {
        const resultTransactions = await this.api_Post("transactions/viewAllTransactionsFilters", { filters: filter });
        if (resultTransactions.status == 200) {
          this.all_transactions = await resultTransactions.json();
        }
      } finally {
        this.loading = false;
      }
    },

    async exportExcel() {
      await global_reports.exportarTransaccionesExcel(this.filteredTransactions);
    },

    askCancel(item) {
      this.selectPayments = item;
      this.dialogCancelPayment = true;
    },
    async confirmCancel() {
      if (!this.selectPayments || this.cancelling) return;
      this.cancelling = true;
      try {
        const responseRequest = await this.api_Post("transactions/cancelTransaction", {
          id: this.selectPayments.payment_transaction_id,
          user_id: this.$store.state.auth.user.user.user_id,
        });
        if (responseRequest.status == 200) {
          this.$toast.success("Transacción anulada!");
          this.dialogCancelPayment = false;
          this.get_transactions(this.lastData);
          this.loadPending();
        } else {
          const error = await responseRequest.json().catch(() => null);
          this.$toast.error(error?.message || "Error al anular transacción");
        }
      } catch (error) {
        this.$toast.error("Error al anular transacción");
      } finally {
        this.cancelling = false;
      }
    },

    async goTransation() {
      if (this.saving) return;
      if (this.lasData == null || !this.lasData.business) {
        this.$toast.warning("Debes elegir una unidad de negocio!");
        return;
      }
      if (!this.lasData.payments_id) {
        this.$toast.warning("Debes elegir un metodo de pago!");
        return;
      }
      if (!this.lasData.accountBank) {
        this.$toast.warning("Debes elegir una cuenta de banco!");
        return;
      }
      if (this.selected.length === 0) {
        this.$toast.warning("Debes elegir un grupo");
        return;
      }
      if (this.selectedTotals.length > 1) {
        this.$toast.warning("Selecciona pagos de una sola moneda");
        return;
      }

      const data = {
        in_payment_transaction_id: null,
        in_user_id: this.$store.state.auth.user.user.user_id,
        in_business_unit_id: this.lasData.business,
        in_bank_account_id: this.lasData.accountBank,
        in_invoice_payment_type_id: this.lasData.payments_id,
        in_payment_date: this.date,
        in_accounting_date: this.date2,
        in_status_id: 1,
        in_transaction_details: this.selected.map((item) => ({ invoice_payment_id: item.invoice_payment_id })),
      };

      this.saving = true;
      try {
        const responseRequest = await this.api_Post("transactions/generate_transactions", data);
        if (responseRequest.status == 200) {
          const rows = await responseRequest.json();
          // La función devuelve el registro compuesto, p. ej. "(1033,123,...)" o un objeto con id
          const created = Array.isArray(rows) && rows[0] ? Object.values(rows[0])[0] : null;
          const createdId = created && typeof created === "object" ? created.id : String(created ?? "").match(/^\(?(\d+)/)?.[1];
          this.createdTransactionId = Number(createdId) > 0 ? Number(createdId) : null;
          this.createdSnackbar = true;
          this.selected = [];
          this.dialog = false;
          this.get_transactions(this.lastData);
          this.loadPending();
        } else {
          const error = await responseRequest.json();
          this.$toast.error(error?.message || error?.error || "Error al registrar la transacción.");
        }
      } catch (error) {
        this.$toast.error("Error al registrar la transacción.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.bg-light-soft {
  background-color: #f8fafc;
}

.border-soft {
  border: 1px solid #f1f5f9 !important;
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9 !important;
}

.border-top {
  border-top: 1px solid #f1f5f9 !important;
}

.shadow-sm {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03) !important;
}

.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}

.shadow-xl {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.custom-table {
  background-color: white !important;
}

.custom-table>>>th {
  background-color: #f8fafc !important;
  text-transform: uppercase;
  font-size: 0.65rem !important;
  font-weight: 800 !important;
  color: #64748b !important;
  letter-spacing: 0.05em;
  height: 48px !important;
}

.custom-table>>>td {
  height: 64px !important;
}

.letter-spacing-1 {
  letter-spacing: 1px;
}

.uppercase {
  text-transform: uppercase;
}

.min-h-screen {
  min-height: 95vh;
}

/* El card del diálogo fullscreen debe cubrir todo el alto del viewport */
.dialog-full-height {
  min-height: 100vh;
  min-height: 100%;
}

.cursor-pointer {
  cursor: pointer;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
}

.sticky-config {
  position: sticky;
  top: 80px;
}

.shadow-xs {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}

.text-tiny {
  font-size: 0.7rem;
}

.hover-shadow {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.hover-shadow:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 20px -10px rgba(0, 0, 0, 0.1), 0 4px 20px 0 rgba(0, 0, 0, 0.05) !important;
}

.transition-all {
  transition: all 0.3s ease;
}

.gap-3 {
  gap: 12px;
}

.min-w-0 {
  min-width: 0;
}

.toolbar-gap {
  gap: 10px;
}

.toolbar-field {
  max-width: 300px;
  min-width: 220px;
}

.toolbar-field--narrow {
  max-width: 230px;
  min-width: 190px;
}

.toolbar-field--wide {
  max-width: 380px;
}

.supplier-cell {
  max-width: 240px;
}

/* Trabajo pendiente por cuenta */
.pending-card {
  height: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  background: #fbfcfe;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.pending-card--active {
  border-color: #1976d2;
  box-shadow: 0 0 0 1px #1976d2 inset;
}

.pending-line {
  display: flex;
  align-items: center;
  padding: 8px;
  margin-top: 6px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #eef2f7;
}

.pending-line--approve {
  cursor: pointer;
  background: #fff8ee;
  border-color: #ffe0b2;
}

.pending-line--approve:hover {
  background: #fff0db;
}

/* Tarjetas de resumen */
.kpi-tile {
  height: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #eef2f7;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.kpi-tile:hover {
  transform: translateY(-2px);
}

.kpi-tile--active {
  border-color: #1976d2;
  box-shadow: 0 0 0 1px #1976d2 inset;
}

.kpi-tile__label {
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #64748b;
}

.kpi-tile__value {
  font-size: 1.6rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.2;
}

.kpi-tile__hint {
  font-size: 0.72rem;
  color: #94a3b8;
}

/* Resumen de pagos seleccionados */
.selected-list {
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #eef2f7;
  border-radius: 10px;
}

.selected-list__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
}

.selected-list__row:last-child {
  border-bottom: none;
}

.cancel-summary {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 10px 14px;
  line-height: 1.9;
  font-size: 0.85rem;
}
/* Acceso a trabajo pendiente */
.pending-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 0;
  padding: 12px 16px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #ffe0b2;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.pending-banner:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px -8px rgba(0, 0, 0, 0.2);
}

.pending-banner--clear {
  border-color: #c8e6c9;
}

.pending-banner__chips {
  gap: 6px;
}

@media (max-width: 600px) {
  .pending-banner .v-btn {
    width: 100%;
    margin-left: 0 !important;
  }
}

.bank-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
